import { motion } from "framer-motion";
import { ArrowDown, Compass, MapPin, Flag } from "lucide-react";
import { Splash } from "./decor";
import { useBooking } from "../BookingContext";

const lineVariants = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.2 + i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const HeroSection = ({ t }) => {
  const { openBooking } = useBooking();
  return (
    <section id="top" className="relative overflow-hidden bg-[#FAFAFA] pb-16 pt-32 lg:pt-40">
      <Splash className="left-[-120px] top-24 h-[420px] w-[420px]" color="#0066FF" />
      <Splash className="right-[-140px] top-40 h-[460px] w-[460px]" color="#FF6B00" delay={0.2} />
      <Splash className="bottom-[-160px] left-1/3 h-[380px] w-[380px]" color="#00C853" delay={0.4} />

      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-6 lg:grid-cols-[1.1fr_0.9fr] lg:px-10">
        <div>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1, duration: 0.6 }}
            className="mb-6 flex items-center gap-3 text-sm font-bold uppercase tracking-[0.35em] text-[#0066FF]"
            data-testid="hero-eyebrow"
          >
            <Compass className="h-4 w-4" /> {t.hero.eyebrow}
          </motion.p>

          <h1 className="font-display text-5xl font-black leading-[0.95] tracking-tight text-black sm:text-6xl lg:text-7xl" data-testid="hero-title">
            {t.hero.titleLines.map((line, i) => (
              <span key={i} className="block overflow-hidden py-0.5">
                <motion.span
                  className="block"
                  custom={i}
                  variants={lineVariants}
                  initial="hidden"
                  animate="visible"
                >
                  {i === t.hero.titleLines.length - 1 ? (
                    <span className="bg-gradient-to-r from-[#0066FF] via-[#FF6B00] to-[#00C853] bg-clip-text text-transparent">
                      {line}
                    </span>
                  ) : (
                    line
                  )}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35, duration: 0.6 }}
            className="mt-7 max-w-xl text-base leading-relaxed text-black/60 lg:text-lg"
            data-testid="hero-body"
          >
            {t.hero.body}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.45, duration: 0.6 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              onClick={() => openBooking("adults")}
              data-testid="hero-book-button"
              className="rounded-full bg-[#FF6B00] px-8 py-4 text-base font-bold text-white shadow-[0_8px_0_0_#c85400] transition-all hover:-translate-y-0.5 hover:shadow-[0_10px_0_0_#c85400]"
            >
              {t.hero.ctaBook}
            </button>
            <a
              href="#method"
              data-testid="hero-method-button"
              className="group flex items-center gap-2 rounded-full border-2 border-black/15 px-7 py-3.5 text-base font-bold text-black transition-colors hover:border-[#0066FF] hover:text-[#0066FF]"
            >
              {t.hero.ctaMethod}
              <ArrowDown className="h-4 w-4 transition-transform group-hover:translate-y-0.5" />
            </a>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6, duration: 0.8 }}
            className="mt-10 max-w-md text-xs font-semibold uppercase tracking-[0.25em] text-black/40"
          >
            {t.hero.tagline}
          </motion.p>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.9, rotate: -3 }}
          animate={{ opacity: 1, scale: 1, rotate: 0 }}
          transition={{ delay: 0.5, duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="relative mx-auto w-full max-w-md"
        >
          <div className="relative rounded-[2rem] border border-black/10 bg-white p-8 shadow-[0_30px_80px_-20px_rgba(0,102,255,0.35)]">
            <img
              src="/mylingomap-logo.png"
              alt="My Lingo Map logo"
              className="w-full object-contain"
              data-testid="hero-logo"
            />
            <div className="mt-4 flex items-center justify-between border-t border-black/10 pt-4">
              <span className="flex items-center gap-2 text-xs font-bold text-[#0066FF]"><MapPin className="h-4 w-4" /> START</span>
              <span className="flex items-center gap-2 text-xs font-bold text-[#00C853]"><Flag className="h-4 w-4" /> FLUENT</span>
            </div>
          </div>
          <motion.div
            animate={{ y: [0, -12, 0] }}
            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            className="absolute -right-4 -top-4 rounded-2xl bg-[#FFD600] px-4 py-3 text-sm font-black text-black shadow-lg"
          >
            1分間
          </motion.div>
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
            className="absolute -bottom-5 -left-5 rounded-2xl bg-[#0066FF] px-4 py-3 text-sm font-black text-white shadow-lg"
          >
            習慣 = 語学
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
